import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { ChevronLeft, ChevronRight, CalendarDays, Plus, Clock, Trash2, Repeat, CalendarOff, Pencil } from "lucide-react";
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, addDays, addWeeks, addMonths, subDays, subWeeks, subMonths, isSameDay, eachDayOfInterval, differenceInMinutes, parseISO } from "date-fns";
import { safeFormat, safeDate } from "@/lib/dateUtils";
import AppointmentDetailsDialog from "@/components/AppointmentDetailsDialog";

const HOUR_HEIGHT = 60;
const START_HOUR = 7;
const END_HOUR = 21;
const TOTAL_HOURS = END_HOUR - START_HOUR;
const DAYS_LIST = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
const DAY_LABELS = { monday:'Mon', tuesday:'Tue', wednesday:'Wed', thursday:'Thu', friday:'Fri', saturday:'Sat', sunday:'Sun' };

function minutesFromGridStart(d) {
  if (!d) return 0;
  const mins = (d.getHours() - START_HOUR) * 60 + d.getMinutes();
  return Math.max(0, Math.min(mins, TOTAL_HOURS * 60));
}

function layoutOverlapping(items) {
  if (!items.length) return items;
  const sorted = [...items].sort((a, b) => a.topPx - b.topPx);
  const columns = [];
  for (const item of sorted) {
    let placed = false;
    for (let c = 0; c < columns.length; c++) {
      const last = columns[c][columns[c].length - 1];
      if (item.topPx >= last.topPx + last.heightPx) {
        columns[c].push(item);
        item._col = c;
        placed = true;
        break;
      }
    }
    if (!placed) {
      item._col = columns.length;
      columns.push([item]);
    }
  }
  const totalCols = columns.length;
  for (const item of sorted) item._totalCols = totalCols;
  return sorted;
}

/* ── Time Off Form (used inside drawer) ── */
function TimeOffForm({ staff, initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial || {
    staff_id: '', title: '', type: 'recurring',
    days_of_week: [], start_time: '09:00', end_time: '17:00',
    start_datetime: '', end_datetime: '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleDay = (day) => {
    set('days_of_week', form.days_of_week.includes(day)
      ? form.days_of_week.filter(d => d !== day)
      : [...form.days_of_week, day]);
  };

  const valid = form.title && (
    form.type === 'recurring' ? form.days_of_week.length > 0 :
    form.start_datetime && form.end_datetime
  );

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-xs font-medium">Title</Label>
        <Input
          data-testid="timeoff-title"
          placeholder="e.g. Pick up Gabriella"
          value={form.title}
          onChange={e => set('title', e.target.value)}
          className="rounded-xl mt-1"
        />
      </div>
      <div>
        <Label className="text-xs font-medium">Staff member</Label>
        <Select value={form.staff_id || 'all'} onValueChange={v => set('staff_id', v === 'all' ? '' : v)}>
          <SelectTrigger className="rounded-xl mt-1" data-testid="timeoff-staff">
            <SelectValue placeholder="All staff (salon-wide)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All staff (salon-wide)</SelectItem>
            {staff.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs font-medium">Type</Label>
        <div className="flex gap-2 mt-1">
          <Button
            data-testid="timeoff-type-recurring"
            variant={form.type === 'recurring' ? 'default' : 'outline'}
            size="sm" className="rounded-xl text-xs gap-1"
            onClick={() => set('type', 'recurring')}
          ><Repeat size={12} /> Recurring</Button>
          <Button
            data-testid="timeoff-type-onetime"
            variant={form.type === 'one-time' ? 'default' : 'outline'}
            size="sm" className="rounded-xl text-xs gap-1"
            onClick={() => set('type', 'one-time')}
          ><CalendarOff size={12} /> One-time</Button>
        </div>
      </div>

      {form.type === 'recurring' ? (
        <>
          <div>
            <Label className="text-xs font-medium">Days</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {DAYS_LIST.map(day => (
                <button
                  key={day}
                  data-testid={`timeoff-day-${day}`}
                  onClick={() => toggleDay(day)}
                  className={`px-2.5 py-1 text-xs rounded-lg border transition-colors ${
                    form.days_of_week.includes(day)
                      ? 'bg-red-100 border-red-300 text-red-700 font-medium'
                      : 'border-border text-muted-foreground hover:border-red-200'
                  }`}
                >{DAY_LABELS[day]}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium">From</Label>
              <Input
                data-testid="timeoff-start-time"
                type="time" value={form.start_time}
                onChange={e => set('start_time', e.target.value)}
                className="rounded-xl mt-1"
              />
            </div>
            <div>
              <Label className="text-xs font-medium">To</Label>
              <Input
                data-testid="timeoff-end-time"
                type="time" value={form.end_time}
                onChange={e => set('end_time', e.target.value)}
                className="rounded-xl mt-1"
              />
            </div>
          </div>
        </>
      ) : (
        <>
          <div>
            <Label className="text-xs font-medium">Start date & time</Label>
            <Input
              data-testid="timeoff-start-datetime"
              type="datetime-local" value={form.start_datetime?.slice(0, 16) || ''}
              onChange={e => set('start_datetime', e.target.value + ':00')}
              className="rounded-xl mt-1"
            />
          </div>
          <div>
            <Label className="text-xs font-medium">End date & time</Label>
            <Input
              data-testid="timeoff-end-datetime"
              type="datetime-local" value={form.end_datetime?.slice(0, 16) || ''}
              onChange={e => set('end_datetime', e.target.value + ':00')}
              className="rounded-xl mt-1"
            />
          </div>
        </>
      )}

      <div className="flex gap-2 pt-2">
        <Button
          data-testid="timeoff-save-btn"
          onClick={() => onSave(form)}
          disabled={!valid}
          className="rounded-xl flex-1"
        >Save</Button>
        <Button variant="outline" onClick={onCancel} className="rounded-xl">Cancel</Button>
      </div>
    </div>
  );
}

/* ── Main Calendar Page ── */
export default function CalendarPage() {
  const [view, setView] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [googleEvents, setGoogleEvents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [filterStaff, setFilterStaff] = useState('all');
  const [showGcal, setShowGcal] = useState(true);

  // Time Off state
  const [timeOffBlocks, setTimeOffBlocks] = useState([]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetMode, setSheetMode] = useState('list'); // 'list' | 'add' | 'edit'
  const [editingBlock, setEditingBlock] = useState(null);

  // Appointment details dialog
  const [detailsAppt, setDetailsAppt] = useState(null);

  const reloadAppts = useCallback(async () => {
    try {
      let dateFrom, dateTo;
      if (view === 'day') {
        dateFrom = format(currentDate, 'yyyy-MM-dd') + 'T00:00:00Z';
        dateTo = format(currentDate, 'yyyy-MM-dd') + 'T23:59:59Z';
      } else if (view === 'week') {
        const ws = startOfWeek(currentDate, { weekStartsOn: 1 });
        const we = endOfWeek(currentDate, { weekStartsOn: 1 });
        dateFrom = format(ws, 'yyyy-MM-dd') + 'T00:00:00Z';
        dateTo = format(we, 'yyyy-MM-dd') + 'T23:59:59Z';
      } else {
        const ms = startOfMonth(currentDate);
        const me = endOfMonth(currentDate);
        dateFrom = format(ms, 'yyyy-MM-dd') + 'T00:00:00Z';
        dateTo = format(me, 'yyyy-MM-dd') + 'T23:59:59Z';
      }
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo, limit: '500' });
      if (filterStaff !== 'all') params.append('staff_id', filterStaff);
      const res = await axios.get(`${API}/appointments?${params}`);
      const apps = res.data.appointments || [];
      setAppointments(apps);
      // Refresh open dialog's data if any
      setDetailsAppt(prev => prev ? apps.find(a => a.id === prev.id) || prev : prev);
    } catch (e) { /* noop */ }
  }, [currentDate, view, filterStaff]);

  const loadTimeOff = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/time-off`);
      setTimeOffBlocks(Array.isArray(res.data) ? res.data : []);
    } catch { setTimeOffBlocks([]); }
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        let dateFrom, dateTo;
        if (view === 'day') {
          dateFrom = format(currentDate, 'yyyy-MM-dd') + 'T00:00:00Z';
          dateTo = format(currentDate, 'yyyy-MM-dd') + 'T23:59:59Z';
        } else if (view === 'week') {
          const ws = startOfWeek(currentDate, { weekStartsOn: 1 });
          const we = endOfWeek(currentDate, { weekStartsOn: 1 });
          dateFrom = format(ws, 'yyyy-MM-dd') + 'T00:00:00Z';
          dateTo = format(we, 'yyyy-MM-dd') + 'T23:59:59Z';
        } else {
          const ms = startOfMonth(currentDate);
          const me = endOfMonth(currentDate);
          dateFrom = format(ms, 'yyyy-MM-dd') + 'T00:00:00Z';
          dateTo = format(me, 'yyyy-MM-dd') + 'T23:59:59Z';
        }
        const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo, limit: '500' });
        if (filterStaff !== 'all') params.append('staff_id', filterStaff);
        const [apptRes, staffRes, gcalRes] = await Promise.all([
          axios.get(`${API}/appointments?${params}`),
          axios.get(`${API}/staff`),
          axios.get(`${API}/calendar/google-events?date_from=${dateFrom}&date_to=${dateTo}`).catch(() => ({ data: { events: [] } }))
        ]);
        setAppointments(apptRes.data.appointments || []);
        setStaff(staffRes.data || []);
        setGoogleEvents(gcalRes.data.events || []);
      } catch (e) {  }
    };
    load();
    loadTimeOff();
  }, [currentDate, view, filterStaff, loadTimeOff]);

  const navigate = (dir) => {
    if (view === 'day') setCurrentDate(d => dir > 0 ? addDays(d, 1) : subDays(d, 1));
    else if (view === 'week') setCurrentDate(d => dir > 0 ? addWeeks(d, 1) : subWeeks(d, 1));
    else setCurrentDate(d => dir > 0 ? addMonths(d, 1) : subMonths(d, 1));
  };

  const statusColors = {
    Confirmed: 'bg-green-200/90 border-green-400 text-green-800',
    Pending: 'bg-yellow-200/90 border-yellow-400 text-yellow-800',
    Completed: 'bg-blue-200/90 border-blue-400 text-blue-800',
    Cancelled: 'bg-red-200/90 border-red-400 text-red-800',
    'No-show': 'bg-gray-200/90 border-gray-400 text-gray-800'
  };
  const hours = Array.from({ length: TOTAL_HOURS }, (_, i) => i + START_HOUR);

  const getDaysForView = () => {
    if (view === 'day') return [currentDate];
    if (view === 'week') {
      const ws = startOfWeek(currentDate, { weekStartsOn: 1 });
      return eachDayOfInterval({ start: ws, end: addDays(ws, 6) });
    }
    return [];
  };

  const getApptsForDay = (day) =>
    appointments.filter(a => {
      const d = safeDate(a.appointment_datetime);
      return d && isSameDay(d, day) && a.booking_status !== 'Cancelled';
    });

  const getExternalGcalEvents = (day) => {
    if (!showGcal) return [];
    return googleEvents.filter(ev => {
      if (ev.all_day) return false;
      const evStart = safeDate(ev.start);
      if (!evStart || !isSameDay(evStart, day)) return false;
      const isBooking = appointments.some(a => a.gcal_event_id === ev.id);
      if (isBooking) return false;
      const summaryLower = (ev.summary || '').toLowerCase();
      const isLikelyBooking = appointments.some(a => {
        if (!isSameDay(safeDate(a.appointment_datetime), day)) return false;
        const svcNames = (a.services || []).map(s => s.name.toLowerCase());
        return svcNames.some(sn => summaryLower.includes(sn));
      });
      return !isLikelyBooking;
    });
  };

  const getAllDayGcalEvents = (day) => {
    if (!showGcal) return [];
    return googleEvents.filter(ev => {
      if (!ev.all_day) return false;
      const evDate = safeDate(ev.start);
      return evDate && isSameDay(evDate, day);
    });
  };

  /* Get time-off blocks that apply to a specific day */
  const getTimeOffForDay = (day) => {
    const dayName = format(day, 'EEEE').toLowerCase();
    return timeOffBlocks.filter(block => {
      if (block.type === 'recurring') {
        return (block.days_of_week || []).includes(dayName);
      }
      // One-time: check if the day falls within the block's date range
      if (block.start_datetime && block.end_datetime) {
        const bStart = safeDate(block.start_datetime);
        const bEnd = safeDate(block.end_datetime);
        if (!bStart || !bEnd) return false;
        const dayStart = new Date(day); dayStart.setHours(0,0,0,0);
        const dayEnd = new Date(day); dayEnd.setHours(23,59,59,999);
        return bStart <= dayEnd && bEnd >= dayStart;
      }
      return false;
    });
  };

  /* Build positioned event items for a day (appts + gcal + time-off) */
  const buildDayEvents = (day) => {
    const items = [];
    // Salon appointments
    for (const a of getApptsForDay(day)) {
      const start = safeDate(a.appointment_datetime);
      if (!start) continue;
      const durMins = a.duration_minutes || 60;
      const topMin = minutesFromGridStart(start);
      const endMin = Math.min(topMin + durMins, TOTAL_HOURS * 60);
      const heightMin = Math.max(endMin - topMin, 15);
      items.push({
        type: 'appt', data: a,
        topPx: (topMin / 60) * HOUR_HEIGHT,
        heightPx: (heightMin / 60) * HOUR_HEIGHT,
        startTime: safeFormat(a.appointment_datetime, 'HH:mm'),
        endTime: safeFormat(new Date(start.getTime() + durMins * 60000).toISOString(), 'HH:mm'),
      });
    }
    // Google Calendar timed events
    for (const ev of getExternalGcalEvents(day)) {
      const start = safeDate(ev.start);
      const end = safeDate(ev.end);
      if (!start) continue;
      const durMins = end ? differenceInMinutes(end, start) : 60;
      const topMin = minutesFromGridStart(start);
      const rawEnd = topMin + Math.max(durMins, 15);
      const endMin = Math.min(rawEnd, TOTAL_HOURS * 60);
      const heightMin = Math.max(endMin - topMin, 15);
      items.push({
        type: 'gcal', data: ev,
        topPx: (topMin / 60) * HOUR_HEIGHT,
        heightPx: (heightMin / 60) * HOUR_HEIGHT,
        startTime: safeFormat(ev.start, 'HH:mm'),
        endTime: end ? safeFormat(ev.end, 'HH:mm') : '',
      });
    }
    return layoutOverlapping(items);
  };

  /* Build time-off overlay blocks for a day (rendered separately as background stripes) */
  const buildTimeOffOverlays = (day) => {
    const dayName = format(day, 'EEEE').toLowerCase();
    const overlays = [];
    for (const block of timeOffBlocks) {
      let startMin = 0, endMin = TOTAL_HOURS * 60;
      if (block.type === 'recurring') {
        if (!(block.days_of_week || []).includes(dayName)) continue;
        const [sh, sm] = (block.start_time || '00:00').split(':').map(Number);
        const [eh, em] = (block.end_time || '23:59').split(':').map(Number);
        startMin = (sh - START_HOUR) * 60 + sm;
        endMin = (eh - START_HOUR) * 60 + em;
      } else {
        const bStart = safeDate(block.start_datetime);
        const bEnd = safeDate(block.end_datetime);
        if (!bStart || !bEnd) continue;
        const dayStart = new Date(day); dayStart.setHours(0,0,0,0);
        const dayEnd = new Date(day); dayEnd.setHours(23,59,59,999);
        if (bStart > dayEnd || bEnd < dayStart) continue;
        // Clamp to this day
        if (isSameDay(bStart, day)) {
          startMin = minutesFromGridStart(bStart);
        }
        if (isSameDay(bEnd, day)) {
          endMin = minutesFromGridStart(bEnd);
        }
      }
      startMin = Math.max(0, startMin);
      endMin = Math.min(TOTAL_HOURS * 60, endMin);
      if (endMin <= startMin) continue;
      const staffName = block.staff_id ? staff.find(s => s.id === block.staff_id)?.name : 'All staff';
      overlays.push({
        id: block.id,
        title: block.title,
        staffName,
        topPx: (startMin / 60) * HOUR_HEIGHT,
        heightPx: ((endMin - startMin) / 60) * HOUR_HEIGHT,
        startTime: block.type === 'recurring' ? block.start_time : safeFormat(block.start_datetime, 'HH:mm'),
        endTime: block.type === 'recurring' ? block.end_time : safeFormat(block.end_datetime, 'HH:mm'),
      });
    }
    return overlays;
  };

  // Time Off CRUD
  const saveTimeOff = async (form) => {
    try {
      if (editingBlock) {
        await axios.put(`${API}/time-off/${editingBlock.id}`, form);
      } else {
        await axios.post(`${API}/time-off`, form);
      }
      await loadTimeOff();
      setSheetMode('list');
      setEditingBlock(null);
    } catch (e) {  }
  };

  const deleteTimeOff = async (id) => {
    try {
      await axios.delete(`${API}/time-off/${id}`);
      await loadTimeOff();
    } catch (e) {  }
  };

  const days = getDaysForView();
  const gridHeight = TOTAL_HOURS * HOUR_HEIGHT;

  return (
    <div className="space-y-4 fade-in">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display' }}>Calendar</h1>
        <div className="flex items-center gap-3">
          <Button
            data-testid="open-timeoff-btn"
            variant="outline" size="sm"
            className="rounded-xl text-xs gap-1.5 border-red-200 text-red-600 hover:bg-red-50"
            onClick={() => { setSheetOpen(true); setSheetMode('list'); }}
          >
            <CalendarOff size={14} /> Time Off
          </Button>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={showGcal} onChange={e => setShowGcal(e.target.checked)} className="rounded" />
            <CalendarDays size={14} className="text-blue-500" />
            Google Calendar
          </label>
          <Select value={filterStaff} onValueChange={setFilterStaff}>
            <SelectTrigger className="w-40 rounded-xl" data-testid="calendar-staff-filter">
              <SelectValue placeholder="All staff" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All staff</SelectItem>
              {staff.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Tabs value={view} onValueChange={setView} data-testid="admin-calendar-view-tabs">
            <TabsList className="rounded-xl">
              <TabsTrigger value="day" className="rounded-lg">Day</TabsTrigger>
              <TabsTrigger value="week" className="rounded-lg">Week</TabsTrigger>
              <TabsTrigger value="month" className="rounded-lg">Month</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs flex-wrap">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-200 border border-green-400" /> Confirmed</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-200 border border-blue-400" /> Completed</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-yellow-200 border border-yellow-400" /> Pending</span>
        {showGcal && <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-indigo-100 border border-indigo-300" /> Google Cal</span>}
        {timeOffBlocks.length > 0 && <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-100 border border-red-300" /> Time Off</span>}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-xl" data-testid="calendar-nav-prev"><ChevronLeft size={18} /></Button>
        <h2 className="text-lg font-semibold" style={{ fontFamily: 'Playfair Display' }}>
          {view === 'day' && format(currentDate, 'EEEE, d MMMM yyyy')}
          {view === 'week' && `${format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'd MMM')} - ${format(endOfWeek(currentDate, { weekStartsOn: 1 }), 'd MMM yyyy')}`}
          {view === 'month' && format(currentDate, 'MMMM yyyy')}
        </h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())} className="rounded-xl text-xs" data-testid="calendar-today-btn">Today</Button>
          <Button variant="ghost" size="icon" onClick={() => navigate(1)} className="rounded-xl" data-testid="calendar-nav-next"><ChevronRight size={18} /></Button>
        </div>
      </div>

      {/* Calendar Grid - Day/Week */}
      {(view === 'day' || view === 'week') && (
        <TooltipProvider>
        <Card className="rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.06)] overflow-hidden" data-testid="calendar-grid">
          <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 280px)' }}>
            <div className="min-w-[600px]">
              {/* Header row */}
              <div className="grid border-b border-border sticky top-0 z-20 bg-white" style={{ gridTemplateColumns: `60px repeat(${days.length}, 1fr)` }}>
                <div className="p-2 text-xs text-muted-foreground" />
                {days.map((day, i) => (
                  <div key={i} className={`p-2 text-center border-l border-border ${isSameDay(day, new Date()) ? 'bg-primary/5' : ''}`}>
                    <p className="text-xs text-muted-foreground">{format(day, 'EEE')}</p>
                    <p className={`text-sm font-semibold ${isSameDay(day, new Date()) ? 'text-primary' : ''}`}>{format(day, 'd')}</p>
                    {getAllDayGcalEvents(day).map(ev => (
                      <div key={ev.id} className="text-[9px] bg-indigo-100 border border-indigo-200 text-indigo-700 rounded px-1 mt-1 truncate">
                        {ev.summary}
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              {/* Time grid body */}
              <div className="grid" style={{ gridTemplateColumns: `60px repeat(${days.length}, 1fr)` }}>
                <div style={{ height: gridHeight, position: 'relative' }}>
                  {hours.map(hour => (
                    <div key={hour} className="text-xs text-muted-foreground text-right pr-2 border-b border-border/50"
                      style={{ height: HOUR_HEIGHT, lineHeight: `${HOUR_HEIGHT}px` }}>
                      {`${hour}:00`}
                    </div>
                  ))}
                </div>

                {days.map((day, di) => {
                  const dayItems = buildDayEvents(day);
                  const timeOffOverlays = buildTimeOffOverlays(day);
                  return (
                    <div key={di}
                      className={`border-l border-border/50 ${isSameDay(day, new Date()) ? 'bg-primary/[0.02]' : ''}`}
                      style={{ height: gridHeight, position: 'relative' }}>
                      {hours.map(hour => (
                        <div key={hour} className="border-b border-border/50" style={{ height: HOUR_HEIGHT }} />
                      ))}

                      {/* Time-off overlay blocks (background) */}
                      {timeOffOverlays.map((ov, idx) => (
                        <Tooltip key={`to-${ov.id}-${idx}`}>
                          <TooltipTrigger asChild>
                            <div
                              data-testid={`calendar-timeoff-${ov.id}`}
                              className="absolute rounded-md overflow-hidden cursor-pointer"
                              style={{
                                top: ov.topPx, height: Math.max(ov.heightPx, 16),
                                left: 1, right: 1, zIndex: 2,
                                background: 'repeating-linear-gradient(135deg, rgba(239,68,68,0.08), rgba(239,68,68,0.08) 4px, rgba(239,68,68,0.15) 4px, rgba(239,68,68,0.15) 8px)',
                                border: '1px solid rgba(239,68,68,0.25)',
                              }}
                            >
                              <div className="px-1 py-0.5 text-[9px] text-red-600 font-medium truncate flex items-center gap-0.5">
                                <CalendarOff size={8} className="shrink-0" />
                                {ov.title}
                              </div>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="font-semibold text-red-600">{ov.title}</p>
                            <p className="text-xs">{ov.startTime} - {ov.endTime}</p>
                            <p className="text-xs text-muted-foreground">{ov.staffName}</p>
                          </TooltipContent>
                        </Tooltip>
                      ))}

                      {/* Event blocks */}
                      {dayItems.map((item, idx) => {
                        const widthPct = 100 / (item._totalCols || 1);
                        const leftPct = (item._col || 0) * widthPct;
                        if (item.type === 'appt') {
                          const a = item.data;
                          return (
                            <Tooltip key={a.id}>
                              <TooltipTrigger asChild>
                                <div data-testid={`calendar-appt-${a.id}`}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => setDetailsAppt(a)}
                                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetailsAppt(a); } }}
                                  className={`absolute rounded-lg border cursor-pointer overflow-hidden text-[10px] leading-tight hover:ring-2 hover:ring-primary/40 transition ${statusColors[a.booking_status] || 'bg-gray-100'}`}
                                  style={{ top: item.topPx, height: Math.max(item.heightPx, 20), left: `calc(${leftPct}% + 2px)`, width: `calc(${widthPct}% - 4px)`, zIndex: 10 }}>
                                  <div className="p-1 h-full flex flex-col">
                                    <p className="font-semibold truncate">{a.client_name}</p>
                                    <p className="truncate opacity-80">{(a.services || []).map(s => s.name).join(', ')}</p>
                                    {item.heightPx > 35 && <p className="truncate opacity-60 mt-auto">{item.startTime} - {item.endTime}</p>}
                                  </div>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p className="font-semibold">{a.client_name}</p>
                                <p className="text-xs">{(a.services || []).map(s => s.name).join(', ')}</p>
                                <p className="text-xs">{item.startTime} - {item.endTime} ({a.duration_minutes}min)</p>
                                <p className="text-xs">{a.payment_status}</p>
                                <p className="text-[10px] text-muted-foreground mt-1">Click to view & edit</p>
                              </TooltipContent>
                            </Tooltip>
                          );
                        }
                        const ev = item.data;
                        return (
                          <Tooltip key={ev.id || idx}>
                            <TooltipTrigger asChild>
                              <div data-testid={`calendar-gcal-${ev.id || idx}`}
                                className="absolute rounded-lg border cursor-pointer overflow-hidden text-[10px] leading-tight bg-indigo-50/90 border-indigo-200 text-indigo-700"
                                style={{ top: item.topPx, height: Math.max(item.heightPx, 20), left: `calc(${leftPct}% + 2px)`, width: `calc(${widthPct}% - 4px)`, zIndex: 5 }}>
                                <div className="p-1 h-full flex flex-col">
                                  <p className="font-semibold truncate flex items-center gap-0.5"><CalendarDays size={8} className="shrink-0" />{ev.summary}</p>
                                  {item.heightPx > 35 && <p className="truncate text-indigo-500 mt-auto">{item.startTime}{item.endTime ? ` - ${item.endTime}` : ''}</p>}
                                </div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p className="font-semibold">{ev.summary}</p>
                              <p className="text-xs">{item.startTime}{item.endTime ? ` - ${item.endTime}` : ''}</p>
                              {ev.description && <p className="text-xs whitespace-pre-wrap line-clamp-4">{ev.description?.substring(0, 200)}</p>}
                              <p className="text-xs text-muted-foreground mt-1">Google Calendar event</p>
                            </TooltipContent>
                          </Tooltip>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Card>
        </TooltipProvider>
      )}

      {/* Month view */}
      {view === 'month' && (
        <Card className="rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.06)] overflow-hidden" data-testid="calendar-month-grid">
          <div className="grid grid-cols-7 border-b border-border">
            {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
              <div key={d} className="p-2 text-xs font-medium text-muted-foreground text-center">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {(() => {
              const monthStart = startOfMonth(currentDate);
              const monthEnd = endOfMonth(currentDate);
              const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
              const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
              const allDays = eachDayOfInterval({ start: calStart, end: calEnd });
              return allDays.map((day, i) => {
                const dayAppts = getApptsForDay(day);
                const dayGcal = getExternalGcalEvents(day);
                const dayTimeOff = getTimeOffForDay(day);
                const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                return (
                  <div key={i} className={`min-h-[80px] p-1 border border-border/30 ${isCurrentMonth ? '' : 'opacity-40'} ${isSameDay(day, new Date()) ? 'bg-primary/5' : ''} ${dayTimeOff.length > 0 ? 'bg-red-50/40' : ''}`}>
                    <p className={`text-xs font-medium mb-1 ${isSameDay(day, new Date()) ? 'text-primary' : 'text-muted-foreground'}`}>{format(day, 'd')}</p>
                    {dayTimeOff.slice(0, 1).map(b => (
                      <div key={b.id} className="text-[9px] p-0.5 px-1 rounded mb-0.5 truncate bg-red-100 text-red-600 border border-red-200">
                        {b.title}
                      </div>
                    ))}
                    {dayAppts.slice(0, 2).map(a => (
                      <div key={a.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => setDetailsAppt(a)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetailsAppt(a); } }}
                        data-testid={`calendar-month-appt-${a.id}`}
                        className={`text-[9px] p-0.5 px-1 rounded mb-0.5 truncate cursor-pointer hover:ring-1 hover:ring-primary/40 ${statusColors[a.booking_status] || 'bg-gray-100'}`}>
                        {safeFormat(a.appointment_datetime, 'HH:mm')} {a.client_name}
                      </div>
                    ))}
                    {dayGcal.slice(0, 1).map(ev => (
                      <div key={ev.id} className="text-[9px] p-0.5 px-1 rounded mb-0.5 truncate bg-indigo-50 text-indigo-700 border border-indigo-100">
                        {ev.all_day ? '\u2022' : safeFormat(ev.start, 'HH:mm')} {ev.summary}
                      </div>
                    ))}
                    {(dayAppts.length + dayGcal.length + dayTimeOff.length) > 4 && (
                      <p className="text-[9px] text-muted-foreground">+{dayAppts.length + dayGcal.length + dayTimeOff.length - 4} more</p>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </Card>
      )}

      {/* Time Off Sheet (Drawer) */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-[380px] sm:w-[420px] overflow-y-auto" data-testid="timeoff-drawer">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2" style={{ fontFamily: 'Playfair Display' }}>
              <CalendarOff size={18} className="text-red-500" />
              {sheetMode === 'list' ? 'Time Off' : sheetMode === 'add' ? 'Add Time Off' : 'Edit Time Off'}
            </SheetTitle>
          </SheetHeader>

          <div className="mt-4">
            {sheetMode === 'list' && (
              <div className="space-y-3">
                <Button
                  data-testid="add-timeoff-btn"
                  onClick={() => { setSheetMode('add'); setEditingBlock(null); }}
                  className="w-full rounded-xl gap-2"
                  size="sm"
                >
                  <Plus size={14} /> Add Time Off
                </Button>

                {timeOffBlocks.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No time-off blocks yet. Add one to block booking slots.
                  </p>
                )}

                {timeOffBlocks.map(block => {
                  const staffName = block.staff_id
                    ? staff.find(s => s.id === block.staff_id)?.name || 'Unknown'
                    : 'All staff';
                  return (
                    <Card key={block.id} className="p-3 rounded-xl border-red-100" data-testid={`timeoff-block-${block.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{block.title}</p>
                          <p className="text-xs text-muted-foreground">{staffName}</p>
                          {block.type === 'recurring' ? (
                            <div className="flex items-center gap-1 mt-1 flex-wrap">
                              <Repeat size={10} className="text-red-400" />
                              {(block.days_of_week || []).map(d => (
                                <span key={d} className="text-[10px] bg-red-50 text-red-600 px-1.5 py-0.5 rounded">
                                  {DAY_LABELS[d] || d}
                                </span>
                              ))}
                              <span className="text-[10px] text-muted-foreground ml-1">
                                {block.start_time} - {block.end_time}
                              </span>
                            </div>
                          ) : (
                            <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
                              <Clock size={10} />
                              {safeFormat(block.start_datetime, 'EEE d MMM HH:mm')} - {safeFormat(block.end_datetime, 'EEE d MMM HH:mm')}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground"
                            data-testid={`edit-timeoff-${block.id}`}
                            onClick={() => {
                              setEditingBlock(block);
                              setSheetMode('edit');
                            }}>
                            <Pencil size={12} />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600"
                            data-testid={`delete-timeoff-${block.id}`}
                            onClick={() => deleteTimeOff(block.id)}>
                            <Trash2 size={12} />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}

            {(sheetMode === 'add' || sheetMode === 'edit') && (
              <TimeOffForm
                staff={staff}
                initial={editingBlock ? {
                  staff_id: editingBlock.staff_id || '',
                  title: editingBlock.title || '',
                  type: editingBlock.type || 'recurring',
                  days_of_week: editingBlock.days_of_week || [],
                  start_time: editingBlock.start_time || '09:00',
                  end_time: editingBlock.end_time || '17:00',
                  start_datetime: editingBlock.start_datetime || '',
                  end_datetime: editingBlock.end_datetime || '',
                } : undefined}
                onSave={saveTimeOff}
                onCancel={() => { setSheetMode('list'); setEditingBlock(null); }}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Appointment Details Dialog (shared with Appointments page) */}
      <AppointmentDetailsDialog
        appointment={detailsAppt}
        open={!!detailsAppt}
        onOpenChange={(o) => { if (!o) setDetailsAppt(null); }}
        onSaved={(updated) => { if (updated) setDetailsAppt(updated); reloadAppts(); }}
      />
    </div>
  );
}
